import { type Brand, make } from "ts-brand";
import { z } from "zod";

/**
 * MessageTemplate domain model — the editable wording of the three outbound
 * notification mails (S1 «Datei bereitgestellt», S3 «Belege eingereicht», S5
 * «Aufgabe gesendet»). One template per {@link MessageTemplateKind}; the
 * per-category TemplateFamily dimension named in the glossar is deferred to a
 * follow-up slice. A TrexpertAdmin edits `subject` and `body`; the selected
 * template is authoritative — there is no longer a per-send override (recorded
 * decision, deviates from glossar «beim Versand überschreibbar»).
 *
 * `subject` and `body` are plain text with `{{placeholder}}` tokens. The server
 * renderer ({@link MessageTemplateAppService}) fills the tokens and derives both
 * the plain-text and the HTML representation, so the editable surface is a single
 * textarea and the admin never authors HTML (injection-safe). Nominal IDs via
 * ts-brand; Zod validates at the boundary.
 */

export type MessageTemplateId = Brand<string, "MessageTemplateId">;
export const MessageTemplateId = make<MessageTemplateId>();

/** The three outbound notification mails, each backed by exactly one template. */
export const MESSAGE_TEMPLATE_KINDS = ["document-provided", "documents-submitted", "task-list-sent"] as const;
export type MessageTemplateKind = (typeof MESSAGE_TEMPLATE_KINDS)[number];
export const MessageTemplateKindSchema = z.enum(MESSAGE_TEMPLATE_KINDS);

/**
 * The placeholder tokens each kind's `subject`/`body` may reference. The update
 * path rejects any template that uses a token outside its kind's set, so an
 * admin can never save a body that would emit a literal `{{token}}` into a
 * customer mail. `recipientFirstName` is the addressee's own first name («Guten
 * Tag {Vorname}»), filled per recipient — even «Aufgabe gesendet», which goes to
 * every member of the customer org, renders one mail per person with that
 * person's name. Some tokens carry server-formatted, multi-line content:
 * `documentList` is the bullet list of files, `customerNote` expands to the
 * whole «Bemerkung des Kunden» block (or nothing when the customer left no note),
 * and `retrievalHint` is the line telling the reader
 * how to reach the documents — «im Anhang» in attachment mode, or a portal link in
 * link-only mode, per the responsible advisor's e-mail preference.
 */
export const ALLOWED_PLACEHOLDERS: Readonly<Record<MessageTemplateKind, readonly string[]>> = {
    "document-provided": ["recipientFirstName", "companyName", "documentList", "portalLink"],
    "documents-submitted": [
        "recipientFirstName",
        "companyName",
        "count",
        "documentList",
        "customerNote",
        "retrievalHint",
    ],
    "task-list-sent": ["recipientFirstName", "companyName", "taskListTitle", "portalLink"],
};

/**
 * The occasions that carry a **library**: several templates, one of them the
 * Vorauswahl, switchable when the trustee sends. `documents-submitted` is
 * deliberately absent — that mail is not triggered by the trustee and goes to the
 * trustee itself, so nobody ever stands at a dialog picking a template for it. It
 * keeps exactly one editable template and no selection. Creating or deleting a
 * template for a non-library kind is rejected at the service boundary.
 */
export const LIBRARY_KINDS = ["document-provided", "task-list-sent"] as const;
export type LibraryKind = (typeof LIBRARY_KINDS)[number];

/** Whether this kind carries a template library (selection + own templates). */
export const isLibraryKind = (kind: MessageTemplateKind): kind is LibraryKind =>
    (LIBRARY_KINDS as readonly string[]).includes(kind);

/** The library label of the template shipped in code — «Standardvorlage» in the UI. */
export const STANDARD_TEMPLATE_TITLE = "Standardtext";

export const MessageTemplateSchema = z.object({
    id: z.string().min(1).transform(MessageTemplateId),
    kind: MessageTemplateKindSchema,
    /** Library label, e.g. «MWST-Belastung». The shipped ones carry {@link STANDARD_TEMPLATE_TITLE}. */
    title: z.string().min(1),
    subject: z.string().min(1),
    body: z.string().min(1),
});
export type MessageTemplate = z.infer<typeof MessageTemplateSchema>;

/**
 * Marker for «the template shipped in code is the Vorauswahl». Kept as its own
 * value rather than a flag on the template row: an unedited Standardvorlage has
 * **no row** (only edited templates are persisted), so it could not carry one —
 * giving it a row would freeze exactly the code text that stays live by design.
 */
export const STANDARD_PRESELECTION = "standard";

/**
 * Which template is preselected when the trustee sends for an occasion: a stored
 * template's id, or {@link STANDARD_PRESELECTION} for the shipped one.
 */
export type Preselection = MessageTemplateId | typeof STANDARD_PRESELECTION;

/**
 * Resolve an occasion's Vorauswahl to the template that will actually be used.
 *
 * {@link STANDARD_PRESELECTION} resolves through `templates` like any other
 * pointer, because «die Standardvorlage» means *the occasion's current standard
 * text* — the stored override once someone edited it, and only then the code
 * text. Resolving it straight to `defaultTemplateFor` would send the shipped
 * wording even though an admin had rewritten it, which is the one thing editing a
 * Standardvorlage is for.
 *
 * Falls back to the shipped template when the pointer names a template that no
 * longer exists — the deletion path resets the pointer, but a stale pointer must
 * never leave an occasion without a usable text.
 */
export const preselectionOf = (
    kind: MessageTemplateKind,
    preselection: Preselection,
    templates: readonly MessageTemplate[]
): MessageTemplate => {
    const wanted = preselection === STANDARD_PRESELECTION ? defaultTemplateFor(kind).id : preselection;
    return templates.find(candidate => candidate.id === wanted) ?? defaultTemplateFor(kind);
};

/** Extract the distinct `{{token}}` names referenced in a piece of template text. */
export const extractPlaceholders = (text: string): string[] => {
    const found = new Set<string>();
    for (const match of text.matchAll(/\{\{(\w+)\}\}/g)) {
        const token = match[1];
        if (token) {
            found.add(token);
        }
    }
    return [...found];
};

/**
 * The placeholder tokens used by `subject`/`body` that are NOT allowed for the
 * given kind. Empty when the template is safe to save. The single home for the
 * allow-list rule so the service function and any UI hint agree.
 */
export const disallowedPlaceholders = (
    kind: MessageTemplateKind,
    subject: string,
    body: string
): string[] => {
    const allowed = new Set(ALLOWED_PLACEHOLDERS[kind]);
    return extractPlaceholders(`${subject}\n${body}`).filter(token => !allowed.has(token));
};

/**
 * The seeded template set — German wording from the recipient's point of view
 * (plan `mail-texte-empfaengersicht`): every mail greets its reader by first name
 * («Guten Tag {Vorname}»), the customer mails are Du-Form and signed «Dein KMU
 * Trexpert AG-Team», the trustee «Belege eingereicht» mail is a system mail signed
 * «KMU Trexpert Portal», and «Mandat» never appears toward a customer (the firm is
 * named instead). The in-memory repository seeds from this; «Auf Standard
 * zurücksetzen» restores a single entry to it.
 */
export const DEFAULT_MESSAGE_TEMPLATES: readonly MessageTemplate[] = [
    {
        id: MessageTemplateId("document-provided"),
        kind: "document-provided",
        title: STANDARD_TEMPLATE_TITLE,
        subject: "Neue Dokumente für Dich bereitgestellt",
        body: [
            "Guten Tag {{recipientFirstName}}",
            "",
            "Im Portal liegen folgende Dokumente für Dich bereit:",
            "{{documentList}}",
            "",
            "Du findest sie jederzeit hier:",
            "{{portalLink}}",
            "",
            "Wir hoffen, Dir damit dienen zu können, und wünschen Dir einen angenehmen Tag.",
            "",
            "Freundliche Grüsse",
            "Dein KMU Trexpert AG-Team",
        ].join("\n"),
    },
    {
        id: MessageTemplateId("documents-submitted"),
        kind: "documents-submitted",
        title: STANDARD_TEMPLATE_TITLE,
        subject: "{{companyName}}: {{count}} eingereicht",
        body: [
            "Guten Tag {{recipientFirstName}}",
            "",
            "{{companyName}} hat über das Portal {{count}} eingereicht:",
            "{{documentList}}{{customerNote}}",
            "",
            "{{retrievalHint}}",
            "",
            "KMU Trexpert Portal",
        ].join("\n"),
    },
    {
        id: MessageTemplateId("task-list-sent"),
        kind: "task-list-sent",
        title: STANDARD_TEMPLATE_TITLE,
        subject: "Neue Aufgabe: {{taskListTitle}}",
        body: [
            "Guten Tag {{recipientFirstName}}",
            "",
            "Für {{companyName}} steht eine neue Aufgabe bereit: «{{taskListTitle}}».",
            "Die Belege kannst Du direkt über folgenden Link hochladen:",
            "{{portalLink}}",
            "",
            "Für Deine Mithilfe danken wir Dir im Voraus bestens und wünschen Dir einen angenehmen Tag.",
            "",
            "Freundliche Grüsse",
            "Dein KMU Trexpert AG-Team",
        ].join("\n"),
    },
];

/**
 * The starter library from the Feedback-Gespräch vom 08.07.2026 — the four texts
 * Dominik dictated (Neue Aufgabe Buchhaltung / Steuererklärung, Dokumente
 * MWST-Belastung / MWST-Guthaben), German only (Entscheid Michael: kein Englisch).
 *
 * Unlike {@link DEFAULT_MESSAGE_TEMPLATES} these are **content, not code**: they
 * get real rows (a data migration on PostgreSQL, the in-memory constructor for
 * dev) and behave like any self-written template — editable *and* deletable. That
 * is the point: a Standardvorlage must stay rowless so a wording change rolls out
 * by deploy, but these belong to the trustee, who may rewrite or discard them.
 * Writing them once via a migration rather than an insert-if-missing boot seed is
 * what makes a deletion stick — the migration runs once per database, a boot seed
 * would resurrect the row on the next restart.
 *
 * The bodies are the customer's own wording, with `[Kundenvorname]` /
 * `[Link zur Aufgabe]` translated into the placeholders their occasion allows and
 * the sign-off aligned to the Du-Form of the shipped texts. The subjects were not
 * dictated and are ours. The MWST texts say «im Anhang» unconditionally — that is
 * the customer's text and the recorded decision (Variante 3: keine bedingten
 * Abschnitte im Renderer); in link-only mode it reads off, and the trustee edits
 * it if that ever matters.
 */
export const INITIAL_LIBRARY_TEMPLATES: readonly MessageTemplate[] = [
    {
        id: MessageTemplateId("initial-aufgabe-buchhaltung"),
        kind: "task-list-sent",
        title: "Neue Aufgabe (Buchhaltung)",
        subject: "Fehlende Unterlagen für Deine Finanzbuchhaltung",
        body: [
            "Guten Tag {{recipientFirstName}}",
            "",
            "Wir konnten Deine Finanzbuchhaltung nachführen. Für die Fertigstellung bitten wir Dich,",
            "uns noch die fehlenden Unterlagen einzureichen. Hierzu kannst Du gleich folgenden Link",
            "anwählen und die Belege hochladen:",
            "{{portalLink}}",
            "",
            "Für Deine Mithilfe danken wir Dir im Voraus bestens und wünschen Dir einen angenehmen Tag.",
            "",
            "Freundliche Grüsse",
            "Dein KMU Trexpert AG-Team",
        ].join("\n"),
    },
    {
        id: MessageTemplateId("initial-aufgabe-steuererklaerung"),
        kind: "task-list-sent",
        title: "Neue Aufgabe (Steuererklärung)",
        subject: "Neue Aufgabe: {{taskListTitle}}",
        body: [
            "Guten Tag {{recipientFirstName}}",
            "",
            "{{portalLink}}",
            "",
            "Wir hoffen, Dir damit dienen zu können, und wünschen Dir einen angenehmen Tag.",
            "",
            "Freundliche Grüsse",
            "Dein KMU Trexpert AG-Team",
        ].join("\n"),
    },
    {
        id: MessageTemplateId("initial-dokumente-mwst-belastung"),
        kind: "document-provided",
        title: "MWST-Belastung",
        subject: "Deine MWST-Abrechnung",
        body: [
            "Guten Tag {{recipientFirstName}}",
            "",
            "Gerne senden wir Dir im Anhang die online eingereichte MWST-Abrechnung.",
            "Wir bitten Dich, den offenen Betrag mit dem Einzahlungsschein auf Seite 2 im Anhang zu",
            "überweisen.",
            "",
            "Wir hoffen, Dir damit dienen zu können, und wünschen Dir einen angenehmen Tag.",
            "",
            "Freundliche Grüsse",
            "Dein KMU Trexpert AG-Team",
        ].join("\n"),
    },
    {
        id: MessageTemplateId("initial-dokumente-mwst-guthaben"),
        kind: "document-provided",
        title: "MWST-Guthaben",
        subject: "Deine MWST-Abrechnung",
        body: [
            "Guten Tag {{recipientFirstName}}",
            "",
            "Gerne senden wir Dir im Anhang die online eingereichte MWST-Abrechnung.",
            "Das Guthaben gemäss Seite 3 im Anhang wird Dir von der Eidg. Steuerverwaltung innert 60",
            "Tagen ausbezahlt.",
            "",
            "Wir hoffen, Dir damit dienen zu können, und wünschen Dir einen angenehmen Tag.",
            "",
            "Freundliche Grüsse",
            "Dein KMU Trexpert AG-Team",
        ].join("\n"),
    },
];

/** The default wording for one kind — used to seed and to reset to default. */
export const defaultTemplateFor = (kind: MessageTemplateKind): MessageTemplate => {
    const template = DEFAULT_MESSAGE_TEMPLATES.find(candidate => candidate.kind === kind);
    if (!template) {
        throw new Error(`No default template for kind ${kind}`);
    }
    return template;
};

/** Boundary input for `updateMessageTemplate` — a TrexpertAdmin edits subject + body. */
export const UpdateMessageTemplateInputSchema = z.object({
    id: z.string().min(1).transform(MessageTemplateId),
    subject: z.string().min(1),
    body: z.string().min(1),
});
export type UpdateMessageTemplateInput = z.infer<typeof UpdateMessageTemplateInputSchema>;

/** Boundary input for `resetMessageTemplate` — restore one kind to its default. */
export const ResetMessageTemplateInputSchema = z.object({
    id: z.string().min(1).transform(MessageTemplateId),
});
export type ResetMessageTemplateInput = z.infer<typeof ResetMessageTemplateInputSchema>;

/**
 * Boundary input for `createMessageTemplate` — a TrexpertAdmin adds a template to
 * an occasion's library. Only {@link LIBRARY_KINDS} are accepted; the service
 * rejects a kind without a library, so `documents-submitted` can never grow a
 * second template.
 */
export const CreateMessageTemplateInputSchema = z.object({
    kind: MessageTemplateKindSchema,
    title: z.string().min(1),
    subject: z.string().min(1),
    body: z.string().min(1),
});
export type CreateMessageTemplateInput = z.infer<typeof CreateMessageTemplateInputSchema>;

/** Boundary input for `deleteMessageTemplate` — removes a self-written template. */
export const DeleteMessageTemplateInputSchema = z.object({
    id: z.string().min(1).transform(MessageTemplateId),
});
export type DeleteMessageTemplateInput = z.infer<typeof DeleteMessageTemplateInputSchema>;

/**
 * Boundary input for `setMessageTemplatePreselection` — which template an occasion
 * preselects when sending. `templateId` carries {@link STANDARD_PRESELECTION} to
 * point back at the shipped text.
 */
export const SetMessageTemplatePreselectionInputSchema = z.object({
    kind: MessageTemplateKindSchema,
    templateId: z.string().min(1),
});
export type SetMessageTemplatePreselectionInput = z.infer<typeof SetMessageTemplatePreselectionInputSchema>;
